import { useState, useEffect, useCallback } from 'react'
import * as Yup from 'yup'
import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid'
import { Slider } from '@/components/ui/slider'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  FileSearch,
  Database,
  Scissors,
  Filter,
  Target,
  Layers,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { documentRAGSettingsSchema } from '@/pages/AdvisorSettings/validation'

interface RAGConfig {
  ragEnabled: boolean
  ragTopK: number
  ragChunkSize: number
  ragChunkOverlap: number
  ragSimilarityThreshold: number
  ragMaxContextLength: number
}

interface RAGConfigSectionProps {
  config: RAGConfig
  onConfigChange: (config: RAGConfig) => void
  disabled?: boolean
}

export default function RAGConfigSection({
  config,
  onConfigChange,
  disabled = false,
}: RAGConfigSectionProps) {
  const [ragEnabled, setRagEnabled] = useState(config.ragEnabled)
  const [topK, setTopK] = useState([config.ragTopK])
  const [chunkSize, setChunkSize] = useState(config.ragChunkSize.toString())
  const [chunkOverlap, setChunkOverlap] = useState(
    config.ragChunkOverlap.toString()
  )
  const [similarityThreshold, setSimilarityThreshold] = useState([
    config.ragSimilarityThreshold,
  ])
  const [maxContextLength, setMaxContextLength] = useState(
    config.ragMaxContextLength.toString()
  )

  // Validation error states
  const [topKError, setTopKError] = useState<string | null>(null)
  const [chunkSizeError, setChunkSizeError] = useState<string | null>(null)
  const [chunkOverlapError, setChunkOverlapError] = useState<string | null>(null)
  const [similarityThresholdError, setSimilarityThresholdError] = useState<string | null>(null)
  const [maxContextLengthError, setMaxContextLengthError] = useState<string | null>(null)

  const configToSchemaShape = useCallback((c: RAGConfig) => ({
    use_full_context: false,
    rag_enabled: c.ragEnabled,
    rag_top_k: c.ragTopK,
    rag_chunk_size: c.ragChunkSize,
    rag_chunk_overlap: c.ragChunkOverlap,
    rag_similarity_threshold: c.ragSimilarityThreshold,
    rag_max_context_length: c.ragMaxContextLength,
  }), [])

  const getValidationErrors = useCallback((c: RAGConfig): Record<string, string> => {
    try {
      documentRAGSettingsSchema.validateSync(configToSchemaShape(c), { abortEarly: false })
      return {}
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const out: Record<string, string> = {}
        for (const e of err.inner) {
          const path = e.path ?? '_form'
          if (!out[path]) out[path] = e.message
        }
        return out
      }
      return {}
    }
  }, [configToSchemaShape])

  const applyValidationErrors = useCallback((errors: Record<string, string>) => {
    setTopKError(errors.rag_top_k ?? null)
    setChunkSizeError(errors.rag_chunk_size ?? null)
    setChunkOverlapError(errors.rag_chunk_overlap ?? null)
    setSimilarityThresholdError(errors.rag_similarity_threshold ?? null)
    setMaxContextLengthError(errors.rag_max_context_length ?? null)
  }, [])

  const buildCurrentConfig = useCallback((): RAGConfig => ({
    ragEnabled,
    ragTopK: topK[0],
    ragChunkSize: parseInt(chunkSize) || 0,
    ragChunkOverlap: parseInt(chunkOverlap) || 0,
    ragSimilarityThreshold: similarityThreshold[0],
    ragMaxContextLength: parseInt(maxContextLength) || 0,
  }), [ragEnabled, topK, chunkSize, chunkOverlap, similarityThreshold, maxContextLength])

  useEffect(() => {
    setRagEnabled(config.ragEnabled)
    setTopK([config.ragTopK])
    setChunkSize(config.ragChunkSize.toString())
    setChunkOverlap(config.ragChunkOverlap.toString())
    setSimilarityThreshold([config.ragSimilarityThreshold])
    setMaxContextLength(config.ragMaxContextLength.toString())
    setTopKError(null)
    setChunkSizeError(null)
    setChunkOverlapError(null)
    setSimilarityThresholdError(null)
    setMaxContextLengthError(null)
  }, [config])

  const handleRagEnabledChange = useCallback(
    (checked: boolean) => {
      setRagEnabled(checked)
      const newConfig: RAGConfig = {
        ragEnabled: checked,
        ragTopK: topK[0],
        ragChunkSize: parseInt(chunkSize) || 0,
        ragChunkOverlap: parseInt(chunkOverlap) || 0,
        ragSimilarityThreshold: similarityThreshold[0],
        ragMaxContextLength: parseInt(maxContextLength) || 0,
      }
      const errors = getValidationErrors(newConfig)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onConfigChange(newConfig)
      }
    },
    [
      topK,
      chunkSize,
      chunkOverlap,
      similarityThreshold,
      maxContextLength,
      onConfigChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleTopKChange = useCallback(
    (value: number[]) => {
      setTopK(value)
      const newConfig: RAGConfig = {
        ragEnabled,
        ragTopK: value[0],
        ragChunkSize: parseInt(chunkSize) || 0,
        ragChunkOverlap: parseInt(chunkOverlap) || 0,
        ragSimilarityThreshold: similarityThreshold[0],
        ragMaxContextLength: parseInt(maxContextLength) || 0,
      }
      const errors = getValidationErrors(newConfig)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onConfigChange(newConfig)
      }
    },
    [
      ragEnabled,
      chunkSize,
      chunkOverlap,
      similarityThreshold,
      maxContextLength,
      onConfigChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleChunkSizeChange = useCallback(
    (value: string) => {
      setChunkSize(value)
      if (value === '') {
        setChunkSizeError('Chunk size is required')
        return
      }
      const numValue = parseInt(value)
      if (isNaN(numValue)) {
        setChunkSizeError('Chunk size must be a number')
        return
      }
      const newConfig: RAGConfig = {
        ragEnabled,
        ragTopK: topK[0],
        ragChunkSize: numValue,
        ragChunkOverlap: parseInt(chunkOverlap) || 0,
        ragSimilarityThreshold: similarityThreshold[0],
        ragMaxContextLength: parseInt(maxContextLength) || 0,
      }
      const errors = getValidationErrors(newConfig)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onConfigChange(newConfig)
      }
    },
    [
      ragEnabled,
      topK,
      chunkOverlap,
      similarityThreshold,
      maxContextLength,
      onConfigChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleChunkOverlapChange = useCallback(
    (value: string) => {
      setChunkOverlap(value)
      if (value === '') {
        setChunkOverlapError('Chunk overlap is required')
        return
      }
      const numValue = parseInt(value)
      if (isNaN(numValue)) {
        setChunkOverlapError('Chunk overlap must be a number')
        return
      }
      const newConfig: RAGConfig = {
        ragEnabled,
        ragTopK: topK[0],
        ragChunkSize: parseInt(chunkSize) || 0,
        ragChunkOverlap: numValue,
        ragSimilarityThreshold: similarityThreshold[0],
        ragMaxContextLength: parseInt(maxContextLength) || 0,
      }
      const errors = getValidationErrors(newConfig)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onConfigChange(newConfig)
      }
    },
    [
      ragEnabled,
      topK,
      chunkSize,
      similarityThreshold,
      maxContextLength,
      onConfigChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleSimilarityThresholdChange = useCallback(
    (value: number[]) => {
      setSimilarityThreshold(value)
      const newConfig: RAGConfig = {
        ragEnabled,
        ragTopK: topK[0],
        ragChunkSize: parseInt(chunkSize) || 0,
        ragChunkOverlap: parseInt(chunkOverlap) || 0,
        ragSimilarityThreshold: value[0],
        ragMaxContextLength: parseInt(maxContextLength) || 0,
      }
      const errors = getValidationErrors(newConfig)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onConfigChange(newConfig)
      }
    },
    [
      ragEnabled,
      topK,
      chunkSize,
      chunkOverlap,
      maxContextLength,
      onConfigChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleMaxContextLengthChange = useCallback(
    (value: string) => {
      setMaxContextLength(value)
      if (value === '') {
        setMaxContextLengthError('Max context length is required')
        return
      }
      const numValue = parseInt(value)
      if (isNaN(numValue)) {
        setMaxContextLengthError('Max context length must be a number')
        return
      }
      const newConfig: RAGConfig = {
        ragEnabled,
        ragTopK: topK[0],
        ragChunkSize: parseInt(chunkSize) || 0,
        ragChunkOverlap: parseInt(chunkOverlap) || 0,
        ragSimilarityThreshold: similarityThreshold[0],
        ragMaxContextLength: numValue,
      }
      const errors = getValidationErrors(newConfig)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onConfigChange(newConfig)
      }
    },
    [
      ragEnabled,
      topK,
      chunkSize,
      chunkOverlap,
      similarityThreshold,
      onConfigChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  return (
    <TooltipProvider>
      <div className='space-y-6'>
        <div className='flex space-x-3'>
          <h2 className='text-2xl font-semibold text-foreground'>
            RAG Configuration
          </h2>
          <Badge variant='secondary' className='text-xs'>
            Default Settings
          </Badge>
        </div>
        <Card>
          <CardHeader className='pb-4'>
            <p className='text-sm text-muted-foreground'>
              Configure default RAG settings for documents. Individual documents can override these settings in the Upload Files section.
            </p>
          </CardHeader>

          <CardContent className='space-y-6'>
            {/* RAG Enable/Disable */}
            <div className='space-y-2'>
              <div className='flex justify-between items-center'>
                <div className='flex gap-2 items-center'>
                  <Database className='w-4 h-4 text-blue-600' />
                  <Label htmlFor='rag-enabled' className='text-sm font-medium'>
                    Enable RAG
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <QuestionMarkCircleIcon className='w-4 h-4 cursor-help text-muted-foreground' />
                    </TooltipTrigger>
                    <TooltipContent className='max-w-xs'>
                      <p>
                        Enable Retrieval-Augmented Generation to use uploaded
                        documents as context for AI responses
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Switch
                  id='rag-enabled'
                  checked={ragEnabled}
                  onCheckedChange={handleRagEnabledChange}
                  disabled={disabled}
                />
              </div>
            </div>

            {/* Top K */}
            <div className={cn('space-y-2', !ragEnabled && 'opacity-50')}>
              <div className='flex justify-between items-center'>
                <div className='flex gap-2 items-center'>
                  <FileSearch className='w-4 h-4 text-green-600' />
                  <Label htmlFor='top-k' className='text-sm font-medium'>
                    Top K Results
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <QuestionMarkCircleIcon className='w-4 h-4 cursor-help text-muted-foreground' />
                    </TooltipTrigger>
                    <TooltipContent className='max-w-xs'>
                      <p>
                        Number of most relevant document chunks to retrieve.
                        Higher values provide more context but may include less
                        relevant information.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <span className='px-2 py-1 font-mono text-sm rounded bg-muted'>
                  {topK[0]}
                </span>
              </div>

              <div className='px-2'>
                <Slider
                  id='top-k'
                  min={1}
                  max={20}
                  step={1}
                  value={topK}
                  onValueChange={handleTopKChange}
                  className='w-full'
                  disabled={disabled || !ragEnabled}
                />
                <div className='flex justify-between mt-1 text-xs text-muted-foreground'>
                  <span>Few (1)</span>
                  <span>Balanced (10)</span>
                  <span>Many (20)</span>
                </div>
              </div>
              {topKError && (
                <p className='text-sm text-red-500 mt-1'>{topKError}</p>
              )}
            </div>

            {/* Chunk Size */}
            <div className={cn('space-y-2', !ragEnabled && 'opacity-50')}>
              <div className='flex gap-2 items-center'>
                <Scissors className='w-4 h-4 text-purple-600' />
                <Label htmlFor='chunk-size' className='text-sm font-medium'>
                  Chunk Size (characters)
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <QuestionMarkCircleIcon className='w-4 h-4 cursor-help text-muted-foreground' />
                  </TooltipTrigger>
                  <TooltipContent className='max-w-xs'>
                    <p>
                      Size of text segments for document processing. Smaller
                      chunks are more precise, larger chunks provide more
                      context per retrieval.
                    </p>
                    <p className='mt-2 font-semibold text-yellow-600'>
                      ⚠️ Changing this requires re-uploading or reprocessing
                      documents to take effect.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className='flex items-center space-x-2'>
                <Input
                  id='chunk-size'
                  type='number'
                  min='100'
                  max='4000'
                  value={chunkSize}
                  onChange={(e) => handleChunkSizeChange(e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setChunkSizeError('Chunk size is required')
                      return
                    }
                    if (isNaN(parseInt(e.target.value))) {
                      setChunkSizeError('Chunk size must be a number')
                      return
                    }
                    applyValidationErrors(getValidationErrors(buildCurrentConfig()))
                  }}
                  className={cn(
                    'w-32',
                    chunkSizeError && 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  )}
                  disabled={disabled || !ragEnabled}
                />
                <div className='flex-1'>
                  <div className='text-xs text-muted-foreground'>
                    Recommended: 500-2000 characters
                  </div>
                </div>
              </div>
              {chunkSizeError && (
                <p className='text-sm text-red-500 mt-1'>{chunkSizeError}</p>
              )}
            </div>

            {/* Chunk Overlap */}
            <div className={cn('space-y-2', !ragEnabled && 'opacity-50')}>
              <div className='flex gap-2 items-center'>
                <Layers className='w-4 h-4 text-orange-600' />
                <Label htmlFor='chunk-overlap' className='text-sm font-medium'>
                  Chunk Overlap (characters)
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <QuestionMarkCircleIcon className='w-4 h-4 cursor-help text-muted-foreground' />
                  </TooltipTrigger>
                  <TooltipContent className='max-w-xs'>
                    <p>
                      Number of overlapping characters between consecutive
                      chunks. Helps maintain context continuity across chunk
                      boundaries.
                    </p>
                    <p className='mt-2 font-semibold text-yellow-600'>
                      ⚠️ Changing this requires re-uploading or reprocessing
                      documents to take effect.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className='flex items-center space-x-2'>
                <Input
                  id='chunk-overlap'
                  type='number'
                  min='0'
                  max='1000'
                  value={chunkOverlap}
                  onChange={(e) => handleChunkOverlapChange(e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setChunkOverlapError('Chunk overlap is required')
                      return
                    }
                    if (isNaN(parseInt(e.target.value))) {
                      setChunkOverlapError('Chunk overlap must be a number')
                      return
                    }
                    applyValidationErrors(getValidationErrors(buildCurrentConfig()))
                  }}
                  className={cn(
                    'w-32',
                    chunkOverlapError && 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  )}
                  disabled={disabled || !ragEnabled}
                />
                <div className='flex-1'>
                  <div className='text-xs text-muted-foreground'>
                    Typically 10-20% of chunk size
                  </div>
                </div>
              </div>
              {chunkOverlapError && (
                <p className='text-sm text-red-500 mt-1'>{chunkOverlapError}</p>
              )}
            </div>

            {/* Similarity Threshold */}
            <div className={cn('space-y-2', !ragEnabled && 'opacity-50')}>
              <div className='flex justify-between items-center'>
                <div className='flex gap-2 items-center'>
                  <Filter className='w-4 h-4 text-cyan-600' />
                  <Label
                    htmlFor='similarity-threshold'
                    className='text-sm font-medium'
                  >
                    Similarity Threshold
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <QuestionMarkCircleIcon className='w-4 h-4 cursor-help text-muted-foreground' />
                    </TooltipTrigger>
                    <TooltipContent className='max-w-xs'>
                      <p>
                        Minimum similarity score (0.0-1.0) for retrieved chunks.
                        Higher values return only highly relevant matches.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <span className='px-2 py-1 font-mono text-sm rounded bg-muted'>
                  {similarityThreshold[0].toFixed(2)}
                </span>
              </div>

              <div className='px-2'>
                <Slider
                  id='similarity-threshold'
                  min={0}
                  max={1}
                  step={0.05}
                  value={similarityThreshold}
                  onValueChange={handleSimilarityThresholdChange}
                  className='w-full'
                  disabled={disabled || !ragEnabled}
                />
                <div className='flex justify-between mt-1 text-xs text-muted-foreground'>
                  <span>Lenient (0.0)</span>
                  <span>Moderate (0.5)</span>
                  <span>Strict (1.0)</span>
                </div>
              </div>
              {similarityThresholdError && (
                <p className='text-sm text-red-500 mt-1'>{similarityThresholdError}</p>
              )}
            </div>

            {/* Max Context Length */}
            <div className={cn('space-y-2', !ragEnabled && 'opacity-50')}>
              <div className='flex gap-2 items-center'>
                <Target className='w-4 h-4 text-red-600' />
                <Label
                  htmlFor='max-context-length'
                  className='text-sm font-medium'
                >
                  Max Context Length (tokens)
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <QuestionMarkCircleIcon className='w-4 h-4 cursor-help text-muted-foreground' />
                  </TooltipTrigger>
                  <TooltipContent className='max-w-xs'>
                    <p>
                      Maximum total length of retrieved document context in
                      tokens. Prevents context from becoming too large for the
                      model.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className='flex items-center space-x-2'>
                <Input
                  id='max-context-length'
                  type='number'
                  min='500'
                  max='8000'
                  value={maxContextLength}
                  onChange={(e) => handleMaxContextLengthChange(e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setMaxContextLengthError('Max context length is required')
                      return
                    }
                    if (isNaN(parseInt(e.target.value))) {
                      setMaxContextLengthError('Max context length must be a number')
                      return
                    }
                    applyValidationErrors(getValidationErrors(buildCurrentConfig()))
                  }}
                  className={cn(
                    'w-32',
                    maxContextLengthError && 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  )}
                  disabled={disabled || !ragEnabled}
                />
                <div className='flex-1'>
                  <div className='text-xs text-muted-foreground'>
                    Recommended: 2000-4000 tokens
                  </div>
                </div>
              </div>
              {maxContextLengthError && (
                <p className='text-sm text-red-500 mt-1'>{maxContextLengthError}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  )
}
